import { Component, ChangeDetectorRef, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { InteractionStatus, EventMessage, EventType, SilentRequest, RedirectRequest, AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { AboutPanelInputs, NavigationItem, SidePanelV3, SidepanelTypesV3, AboutPanelComponent } from '@gea/digital-ui-lib';
import { TranslateService } from '@ngx-translate/core';
import { filter, Subject, takeUntil } from 'rxjs';
import { SpinnerService } from './pages/core/shared/services/spinner.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss'
})
export class AppComponent {
  title = 'NPE Engineering Portal';
  isIframe = false;
  loginDisplay = false;
  time: any;
  timeout: number = 600000;
  showSubSubMenu: boolean = false;
  userLogin: any;
  // <CHANGE ME> - Add the navigation items for the Blueprint App
  navItems: NavigationItem[] = [];

  adminNavItems: NavigationItem[] = [];
  botItems: SidePanelV3[] = [];
  private readonly _destroying$ = new Subject<void>();
  private tokenRedirectInProgress = false;

  constructor(@Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    public translate: TranslateService,
    public spinnerService: SpinnerService,
    private cdr: ChangeDetectorRef) {
    translate.addLangs(['en-US', 'es-ES']);
    translate.setDefaultLang('en-US');
    let browserLang = translate.getBrowserLang();
    translate.use(browserLang?.match(/en-US|es-ES/) ? browserLang : 'en-US');
  }
  ngOnInit(): void {
    this.isIframe = window !== window.parent && !window.opener;
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None && !this.tokenRedirectInProgress),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
        this.setLoginDisplay();
        if (this.loginDisplay) {
          this.acquireTokenSilent();
        }
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          msg.eventType === EventType.LOGIN_SUCCESS ||
          msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
          msg.eventType === EventType.SSO_SILENT_SUCCESS
        )
      )
      .subscribe((result: EventMessage) => {
        const payload = (result as any).payload;

        if (payload) {
          const roles = JSON.stringify(payload.idTokenClaims?.roles || []);
          sessionStorage.setItem('idtoken', payload.idToken);
          sessionStorage.setItem('account', JSON.stringify(payload.account));
          sessionStorage.setItem('accessToken', payload.accessToken);
          sessionStorage.setItem('roles', roles);
          sessionStorage.setItem('username', payload.account?.username || '');
          this.navItems = [];
          this.botItems = [];

          if (roles.includes("user") || roles.includes("admin")) {

            this.navItems.push({
              label: 'UI-LIB.SIDE-BAR.ControlSystems',
              route: '/control-systems',
              icon: 'apps',
            });
          }
          if (roles.includes("user") || roles.includes("admin")) {

            this.navItems.push({
              label: 'UI-LIB.SIDE-BAR.Customer',
              route: '/admin/customer',
              icon: 'factory',
            });

          }
          if (roles.includes("codexadmin") || roles.includes("codexautmanager") || roles.includes("codexengineer") || roles.includes("codexuser")) {
            this.navItems.push({
              label: 'UI-LIB.SIDE-BAR.CodexProjectsInfo',
              route: '/admin/codex-projects-info',
              icon: 'bullet-list-70',
            });

          }



          this.botItems.push({
            key: 'manuals',
            icon: 'folder',
            label: 'Manuals',
            type: SidepanelTypesV3.ROUTER,
            router: {
              url: '/admin/manuals',
              target: '/admin/manuals'
            }
          });


          if (roles.includes("superadmin")) {
            this.botItems.push({
              key: 'settings',
              icon: 'gear',
              label: 'Settings',
              type: SidepanelTypesV3.ROUTER,
              router: {
                url: "/admin/settings",
                target: "/admin/settings"
              }
            });
          }

          if (roles.includes("superadmin")) {
            this.botItems.push({
              key: 'api-docs',
              icon: 'puzzle',
              label: 'API Docs',
              type: SidepanelTypesV3.ROUTER,
              router: {
                url: "/admin/api-docs",
                target: "/admin/api-docs"
              }
            });
          }
          this.authService.instance.setActiveAccount(payload.account);
          setTimeout(() => {
            this.userLogin = payload.account?.username || '';
            this.cdr.detectChanges();
          }, 0);
        }
      });



  }


  acquireTokenSilent() {
    if (this.tokenRedirectInProgress) return;

    const account = this.authService.instance.getActiveAccount();

    if (!account) {
      console.log("No hay cuenta activa, el usuario debe autenticarse");
      return;
    }

    const request: SilentRequest = {
      scopes: environment.apiScopes,
      account: account
    };

    this.authService.acquireTokenSilent(request).subscribe({
      next: (response) => {
        // Guardar en sessionStorage
        sessionStorage.setItem('accessToken', response.accessToken);
        sessionStorage.setItem('idtoken', response.idToken);
        sessionStorage.setItem('username', account.username);
        const roles = (response.idTokenClaims as any)?.roles || [];
        sessionStorage.setItem('roles', JSON.stringify(roles));

        this.authService.instance.setActiveAccount(account);
        setTimeout(() => {
          this.userLogin = account.username;
          this.cdr.detectChanges();
        }, 0);

        console.log("User Autenticate:", this.userLogin);
      },
      error: (error) => {
        const authorityMismatch = error?.errorCode === 'authority_mismatch';

        if (!(error instanceof InteractionRequiredAuthError) && !authorityMismatch) {
          console.error('Silent token acquisition failed', {
            errorCode: error?.errorCode ?? 'unknown_error',
            subError: error?.subError,
            correlationId: error?.correlationId
          });
          return;
        }

        if (this.tokenRedirectInProgress) return;
        this.tokenRedirectInProgress = true;

        const redirectRequest: RedirectRequest = authorityMismatch
          ? {
            scopes: environment.apiScopes,
            authority: environment.msalConfig.auth.authority,
            prompt: 'select_account'
          }
          : { scopes: environment.apiScopes, account };

        if (authorityMismatch) {
          this.authService.instance.setActiveAccount(null);
        }

        this.authService.acquireTokenRedirect(redirectRequest)
          .pipe(takeUntil(this._destroying$))
          .subscribe({
            error: (redirectError) => {
              this.tokenRedirectInProgress = false;
              console.error('Interactive token acquisition failed', {
                errorCode: redirectError?.errorCode ?? 'unknown_error',
                subError: redirectError?.subError,
                correlationId: redirectError?.correlationId
              });
            }
          });
      }
    });
  }


  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);

    }
  }
  loginRedirect() {
    console.log("Intentando redirigir al login...");
    const redirectStartPage = window.location.href;
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest as RedirectRequest, redirectStartPage });
    } else {
      this.authService.loginRedirect({ scopes: environment.apiScopes, redirectStartPage });
    }
  }

  get Username(): string {
    let userInfo: AccountInfo = this.authService.instance.getAllAccounts()[0];
    return userInfo ? userInfo.username : "";
  }
  get Account(): AccountInfo | null {
    let userInfo = this.authService.instance.getAccountByUsername(this.Username);
    return userInfo;

  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }






  logout() {

    this.authService.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
    sessionStorage.clear();

  }
}
